import { useEffect, useState, useMemo } from "react";
import "./MindMappingReportBFourthSection.css";

const QTag = ({ q, a, impacts }) => (
  <div style={{display:'flex',flexWrap:'wrap',gap:'5px 10px',alignItems:'center',background:'#f0f4ff',border:'1px solid #c7d7fa',borderRadius:'6px',padding:'7px 12px',marginBottom:'12px',fontSize:'12px',lineHeight:1.5}}>
    <span style={{background:'#3b5bdb',color:'#fff',fontWeight:700,padding:'2px 8px',borderRadius:4,fontSize:'10px',whiteSpace:'nowrap'}}>📋 FORM QUESTION</span>
    <span style={{color:'#1e3a8a',fontStyle:'italic'}}>"{q}"</span>
    <span style={{color:'#6b7280',fontSize:'11px'}}>→</span>
    <span style={{color:'#047857',fontWeight:700}}>You answered: <strong>{a}</strong></span>
    <span style={{color:'#6b7280',fontSize:'11px'}}>→</span>
    <span style={{color:'#7c3aed',fontSize:'11px'}}>📊 Impacts: {impacts}</span>
  </div>
);

const MindMappingReportBFourthSection = ({ res,toggle }) => {
  // 🔑 STATE MUST HOLD THE KEY, NOT DISPLAY TEXT
  const [selectedFunds, setSelectedFunds] = useState("0.5");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 767);

  // 🔁 Mapping based on FUND IDENTIFIER
  const fundsMapping = useMemo(() => ({
    "0.5": {
      display: "< 0.5 M",
      profile: "Bootstrapped / personal savings",
      advantages: "High discipline, strong validation mindset",
      disadvantages: "Slow growth, resource constraints",
      accessibility: "⭐⭐⭐ Medium",
      investor: "⚠️ Neutral—Positive (if traction shown)",
      bestFit: "Services, MVPs, Freelance platforms",
    },
    "1-2": {
      display: "1-2 M",
      profile: "Early-stage self-funded",
      advantages: "Enough for MVP + early traction",
      disadvantages: "Limited runway",
      accessibility: "⭐⭐⭐⭐ High",
      investor: "✅ Positive (capital-efficient founders)",
      bestFit: "SaaS MVPs, D2C pilots, Marketplaces",
    },
    "5-7": {
      display: "5-7 M",
      profile: "Strong early commitment",
      advantages: "Faster execution, early hiring",
      disadvantages: "Higher burn risk",
      accessibility: "⭐⭐⭐⭐ High",
      investor: "🏆 Very Positive",
      bestFit: "SaaS, Tech platforms, FinTech",
    },
    "10-20": {
      display: "10-20 M",
      profile: "Serious scale intention",
      advantages: "Strong market entry, speed advantage",
      disadvantages: "Risk of overspending",
      accessibility: "⭐⭐⭐\n⭐⭐ \n Very High",
      investor: "👑 Highly Attractive",
      bestFit: "VC-scale startups, Platforms",
    },
    "100": {
      display: "> 100 M",
      profile: "Institutional or HNI-backed",
      advantages: "Market dominance, barriers to entry",
      disadvantages: "Lower capital efficiency focus",
      accessibility: "⭐⭐⭐ \n⭐⭐ \n Very High",
      investor: "🎯 Selective (expects large outcomes)",
      bestFit: "Infra, Manufacturing, Large platforms",
    },
  }), []);

  // 🔥 CORE FIX — READ affordability (NOT initial_funds)
  useEffect(() => {
    if (!res?.affordability) return;

    const backendVal = String(res.affordability);

    const foundKey = Object.keys(fundsMapping).find((key) =>
      backendVal.includes(key)
    );

    if (foundKey) {
      setSelectedFunds(foundKey);
    } else {
      // fallback safety
      setSelectedFunds("0.5");
    }
  }, [res, fundsMapping]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 767);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const activeData =
    fundsMapping[selectedFunds] || fundsMapping["0.5"];

  return (
    <div className="mind-mapping-report-b-fourth-container">
      <div className={toggle?"table-adjust":"mind-mapping-report-b-fourth-final-table"}> 
        <span className="mind-mapping-report-b-fourth-corner mind-mapping-report-b-fourth-corner-top-left"></span>
        <span className="mind-mapping-report-b-fourth-corner mind-mapping-report-b-fourth-corner-top-right"></span>
        <span className="mind-mapping-report-b-fourth-corner mind-mapping-report-b-fourth-corner-bottom-left"></span>
        <span className="mind-mapping-report-b-fourth-corner mind-mapping-report-b-fourth-corner-bottom-right"></span>

        <div className="mind-mapping-report-b-fourth-module-title">
           <span className="module-prefix">Module 4:</span> Initial Funds vs Startup & Investor Perspective
        </div>
        <QTag
          q="How much initial capital can you commit to launching your startup?"
          a={`${activeData.display} (${activeData.profile})`}
          impacts="Runway length, team size, go-to-market speed, investor signaling, and burn rate discipline"
        />
        <div className="mind-mapping-report-b-fourth-table-scroll-wrapper">
          <table className="mind-mapping-report-b-fourth-inner-table">
            <thead>
              <tr>
                <th className="mind-mapping-report-b-fourth-col-sno">
                  S.NO
                </th>
                <th>Initial Funds</th>
                <th>Founder Profile</th>
                <th>Key Advantages</th>
                <th>Key Disadvantages</th>
                <th>Accessibility</th>
                <th>Investor Perspective</th>
                <th>Best-Fit Types</th>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td className="mind-mapping-report-b-fourth-col-sno">
                  4 A
                </td>
                <td className="mind-mapping-report-b-fourth-user-val">
                  {activeData.display}
                </td>
                <td>{activeData.profile}</td>
                <td>{activeData.advantages}</td>
                <td>{activeData.disadvantages}</td>
                <td>
                  {isMobile ? (
                    activeData.accessibility.split('\n').map((line, index) => (
                      <span key={index}>
                        {line}
                        {index < activeData.accessibility.split('\n').length - 1 && <br />}
                      </span>
                    ))
                  ) : (
                    activeData.accessibility
                  )}
                </td>
                <td>{activeData.investor}</td>
                <td className="mind-mapping-report-b-fourth-highlight">
                  {activeData.bestFit}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MindMappingReportBFourthSection;
